import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { usePerspective } from '../contexts/PerspectiveContext';
import {
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Button
} from '@mui/material';
import { MENU_TEXT_TO_MODULE_KEY, PATH_TO_MODULE_KEY } from '../constants/permissions';
import { usePermissions } from '../hooks/usePermissions';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  SpaceDashboard as SpaceDashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Science as MaterialsIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  Store as StoreIcon,
  Assessment as AnalyticsIcon,
  LocalOffer as PromoIcon,
  PersonAdd as RepManagementIcon,
  Biotech as BiotechIcon,
  CalendarToday as CalendarIcon,
  SwapHoriz as TransferIcon,
  AccountBalance as AccountingIcon,
  Folder as FolderIcon,
  ManageSearch as AuditIcon,
  AccountCircle as UserSettingsIcon,
  Checklist as OpsIcon,
  MarkEmailRead as EmailLogIcon,
} from '@mui/icons-material';
import { DS } from './ds/tokens';
import { adminAPI } from '../services/api';
import { logger } from '../utils/logger';

const drawerWidth = 280;

// Attention-badge colors by worst severity in a nav item's queue. Critical red
// matches the client sidebar's unpaid badge (#d61616); squared to match the
// internal "Access" badges (borderRadius 0).
const SEVERITY_BG = { critical: '#d61616', warning: '#d97706', info: '#009c87' };

const InternalNavigation = ({ children, currentUser, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const { org, clearOrg } = useOrg();
  const { clearPerspective } = usePerspective();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentCompany = org; // always 'epl'
  const { canAccessModule } = usePermissions(currentUser);

  useEffect(() => {
    if (!currentCompany || !location.pathname) return;
    const prefix = `/internal/${currentCompany}/`;
    if (!location.pathname.startsWith(prefix)) return;
    const pathAfterOrg = location.pathname.slice(prefix.length);
    const match = Object.entries(PATH_TO_MODULE_KEY).find(([pathSegment]) =>
      pathAfterOrg === pathSegment || pathAfterOrg.startsWith(pathSegment + '/')
    );
    if (match) {
      const moduleKey = match[1];
      if (!canAccessModule(moduleKey)) {
        navigate(`/internal/${currentCompany}/dashboard`, { replace: true });
      }
    }
  }, [location.pathname, currentCompany, canAccessModule, navigate]);

  // Sidebar attention badges — count per nav item that has a "needs action"
  // queue. Refetch on route change so a badge clears once the work is handled
  // (mirrors the client sidebar's unpaid-invoice badge). Only items with a
  // non-empty queue come back, so a badge shows ONLY when something needs action.
  const [badgeCounts, setBadgeCounts] = useState({});
  useEffect(() => {
    if (!currentCompany) return;
    let cancelled = false;
    adminAPI.getSidebarBadges(currentCompany)
      .then((res) => { if (!cancelled && res?.success) setBadgeCounts(res.data || {}); })
      .catch((err) => logger.error('Sidebar badge fetch failed:', err));
    return () => { cancelled = true; };
  }, [currentCompany, location.pathname]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDesktopDrawerClose = () => setDesktopOpen(false);
  const handleDesktopDrawerOpen = () => setDesktopOpen(true);

  const handleLogout = () => {
    clearOrg();
    clearPerspective();
    onLogout();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <SpaceDashboardIcon />, path: `/internal/${currentCompany}/dashboard` },
    { text: 'Order Processing', icon: <OrdersIcon />, path: `/internal/${currentCompany}/order-processing` },
    { text: 'Transfer Orders', icon: <TransferIcon />, path: `/internal/${currentCompany}/transfer-orders` },
    { text: 'Raw Materials', icon: <MaterialsIcon />, path: `/internal/${currentCompany}/raw-materials` },
    { text: 'Donor Management', icon: <BiotechIcon />, path: `/internal/${currentCompany}/donor-management` },
    { text: 'Receive Inventory', icon: <InventoryIcon />, path: `/internal/${currentCompany}/receive-inventory` },
    { text: 'EPL Calendar', icon: <CalendarIcon />, path: `/internal/${currentCompany}/admin/calendar` },
    { text: 'Promo Codes', icon: <PromoIcon />, path: `/internal/${currentCompany}/admin/promo-codes` },
    { text: 'Client Management', icon: <GroupIcon />, path: `/internal/${currentCompany}/client-management` },
    { text: 'Rep Management', icon: <RepManagementIcon />, path: `/internal/${currentCompany}/admin/rep-management` },
    { text: 'Accounting', icon: <AccountingIcon />, path: `/internal/${currentCompany}/admin/accounting` },
    { text: 'Horses', icon: <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box component="img" src={`${process.env.PUBLIC_URL}/Assets/horseVector.png`} alt="" sx={{ width: 14, height: 15, objectFit: 'contain', opacity: 0.85 }} /></Box>, path: `/internal/${currentCompany}/horses` },
    { text: 'Reports', icon: <AnalyticsIcon />, path: `/internal/${currentCompany}/client-analytics` },
    { text: 'Resources', icon: <FolderIcon />, path: `/internal/${currentCompany}/admin/resources` },
    { text: 'Audit Logs', icon: <AuditIcon />, path: `/internal/${currentCompany}/admin/audit-logs` },
    { text: 'Purchase Orders', icon: <StoreIcon />, path: `/internal/${currentCompany}/purchase-orders` },
    // Operations tab hidden 2026-06-10 (Drew): nobody uses it. Route + backend
    // routes left in place so we can flip the menu item back on without
    // re-implementing if the team revives the planning surface later.
    // { text: 'Operations', icon: <OpsIcon />, path: `/internal/${currentCompany}/operations` },
    // Email Log replaces Operations on the admin side. Hidden for non-super
    // admins below in the post-filter so it never leaks via "show all when no
    // permissions" default behavior.
    { text: 'Email Log', icon: <EmailLogIcon />, path: `/internal/${currentCompany}/admin/email-log` },
    { text: 'User Settings', icon: <UserSettingsIcon />, path: `/internal/${currentCompany}/user-settings` },
    { text: 'Admin Settings', icon: <SettingsIcon />, path: `/internal/${currentCompany}/settings` }
  ];

  const isSuperAdmin = (
    currentUser?.isSuperAdmin === true ||
    (typeof currentUser?.organization === 'string' && currentUser.organization.toUpperCase() === 'BOTH')
  );

  const filteredMenuItems = (() => {
    if (currentUser?.isSuperAdmin === true ||
        (typeof currentUser?.organization === 'string' && currentUser.organization.toUpperCase() === 'BOTH')) {
      return menuItems;
    }
    const perms = currentUser?.profile?.permissions || currentUser?.permissions;
    if (!perms || Array.isArray(perms) || !perms.modules) {
      return menuItems;
    }
    return menuItems.filter(item => {
      const moduleKey = MENU_TEXT_TO_MODULE_KEY[item.text];
      if (!moduleKey) return true;
      const modulePerm = perms.modules[moduleKey];
      if (!modulePerm) return true;
      return modulePerm.visible === true;
    });
  })();

  // Explicit opt-in for the Operations tab (STRICT, 2026-04-24).
  //
  // The filter above defaults to "show all" when the user has no structured
  // permissions, which would leak the Operations tab to every user. We do NOT
  // refactor that filter -- we layer ONE extra rule on top, affecting only
  // the Operations item: it's hidden unless `currentUser.opsRole` is truthy.
  //
  // There is NO super-admin fallback. Portal super-admins who aren't in
  // ops_roles will not see the tab, same as any other user -- they must be
  // granted a role via Manage Access. The server-side requireOpsAnyRole
  // guard enforces the same rule on /api/operations/* (with the 404
  // response that mimics the unmatched-route handler).
  //
  // opsRole is populated by /api/auth/login, /api/auth/verify-code, and
  // /api/auth/me. Stale sessions see the tab hidden until the next /me
  // refresh repopulates currentUser.opsRole in localStorage.
  const finalMenuItems = filteredMenuItems.filter((item) => {
    // Operations is fully commented out in the menu source above, but keep
    // the opsRole guard here as a belt-and-suspenders measure in case
    // someone uncomments it without thinking.
    if (item.text === 'Operations' && !currentUser?.opsRole) return false;
    // Email Log is super-admin-only. Mirrors the backend requireAdmin gate.
    if (item.text === 'Email Log' && !isSuperAdmin) return false;
    return true;
  });

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: DS.surface,
      fontFamily: DS.fontFamily,
      borderRight: `1px solid ${DS.border}`
    }}>
      {/* Header */}
      <Box sx={{
        px: 2.5,
        pt: 2.5,
        pb: 2,
        borderBottom: `1px solid ${DS.border}`,
        position: 'relative'
      }}>
        {!isMobile && (
          <IconButton
            onClick={handleDesktopDrawerClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: DS.textSecondary,
              width: 28,
              height: 28,
              borderRadius: 0,
              '&:hover': {
                color: DS.textPrimary,
                bgcolor: DS.surfaceSoft
              }
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        {/* Org label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AdminIcon sx={{ fontSize: 16, color: DS.primary }} />
          <Typography sx={{
            fontFamily: DS.fontFamily,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: DS.primary
          }}>
            Internal — EPL
          </Typography>
        </Box>

        {/* User row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Avatar sx={{
            width: 36,
            height: 36,
            bgcolor: DS.surfaceSoft,
            color: DS.textPrimary,
            fontFamily: DS.fontFamily,
            fontWeight: 600,
            fontSize: 13,
            border: `1px solid ${DS.border}`,
            borderRadius: 0
          }}>
            {(currentUser?.name || 'Admin').split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{
              fontFamily: DS.fontFamily,
              fontWeight: 600,
              fontSize: 14,
              color: DS.textPrimary,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {currentUser?.name || 'Admin User'}
            </Typography>
            <Typography sx={{
              fontFamily: DS.fontFamily,
              fontSize: 12,
              color: DS.textSecondary,
              lineHeight: 1.4
            }}>
              EPL Operations
            </Typography>
          </Box>
        </Box>

        {/* Access badges */}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {['Admin', 'Live'].map(label => (
            <Box key={label} sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 20,
              px: 1,
              borderRadius: 0,
              border: `1px solid ${DS.primary}`,
              color: DS.primary,
              bgcolor: 'transparent',
              fontFamily: DS.fontFamily,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.04em'
            }}>
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 1, py: 1, flexGrow: 1, overflowY: 'auto' }}>
        {finalMenuItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 0,
                  py: 0.875,
                  pl: active ? '13px' : '16px',
                  pr: 1.5,
                  minHeight: 0,
                  position: 'relative',
                  borderLeft: active ? `3px solid ${DS.primary}` : '3px solid transparent',
                  bgcolor: active ? DS.surfaceSoft : 'transparent',
                  color: active ? DS.textPrimary : DS.textSecondary,
                  transition: 'background-color 150ms ease, color 150ms ease',
                  '&:hover': {
                    bgcolor: DS.surfaceSoft,
                    color: DS.textPrimary
                  }
                }}
              >
                <ListItemIcon sx={{
                  color: 'inherit',
                  minWidth: 28,
                  '& .MuiSvgIcon-root': { fontSize: 18 }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontFamily: DS.fontFamily,
                    fontSize: 14,
                    fontWeight: active ? 600 : 500,
                    color: 'inherit'
                  }}
                />
                {(() => {
                  const badge = badgeCounts[item.text];
                  if (!badge || !badge.count) return null;
                  return (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: SEVERITY_BG[badge.severity] || SEVERITY_BG.critical,
                        color: '#ffffff',
                        fontFamily: DS.fontFamily,
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 1,
                        px: '5px',
                        ml: 1,
                        minWidth: 18,
                        height: 16,
                        borderRadius: 0,
                        flexShrink: 0,
                      }}
                    >
                      {badge.count > 99 ? '99+' : badge.count}
                    </Box>
                  );
                })()}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Action buttons */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Button
          fullWidth
          startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
          onClick={handleLogout}
          size="small"
          sx={{
            bgcolor: 'transparent',
            color: '#c0392b',
            border: '1px solid rgba(192,57,43,0.35)',
            fontFamily: DS.fontFamilyButton,
            fontWeight: 600,
            fontSize: 13,
            py: 0.75,
            borderRadius: 0,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'rgba(192,57,43,0.06)',
              borderColor: '#c0392b'
            }
          }}
        >
          Sign Out
        </Button>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${DS.border}` }}>
        <Typography sx={{
          fontFamily: DS.fontFamily,
          fontSize: 11,
          fontWeight: 500,
          color: DS.textSecondary,
          textAlign: 'center',
          lineHeight: 1.4
        }}>
          © 2024 UnisynHub
        </Typography>
        <Typography sx={{
          fontFamily: DS.fontFamily,
          fontSize: 10,
          color: DS.textSecondary,
          opacity: 0.7,
          textAlign: 'center',
          lineHeight: 1.4
        }}>
          EPL Operations
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="nav"
        sx={{
          width: 0,
          flexShrink: 0,
          overflow: 'hidden'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: DS.surface,
              borderRight: `1px solid ${DS.border}`
            }
          }}
        >
          {drawer}
        </Drawer>

        {!isMobile && desktopOpen && (
          <Drawer
            variant="permanent"
            open={desktopOpen}
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                position: 'fixed',
                height: '100vh',
                top: 0,
                left: 0,
                zIndex: 1200,
                bgcolor: DS.surface,
                borderRight: `1px solid ${DS.border}`
              }
            }}
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      {/* Mobile menu toggle */}
      <IconButton
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1300,
          display: { md: 'none' },
          bgcolor: DS.surface,
          color: DS.textPrimary,
          width: 40,
          height: 40,
          borderRadius: 0,
          border: `1px solid ${DS.border}`,
          '&:hover': { bgcolor: DS.surfaceSoft }
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Desktop open toggle (when closed) */}
      {!isMobile && !desktopOpen && (
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDesktopDrawerOpen}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            bgcolor: DS.surface,
            color: DS.textPrimary,
            width: 40,
            height: 40,
            borderRadius: 0,
            border: `1px solid ${DS.border}`,
            '&:hover': { bgcolor: DS.surfaceSoft }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          marginLeft: {
            xs: 0,
            md: desktopOpen ? `${drawerWidth}px` : 0
          },
          transition: 'margin-left 0.3s ease',
          backgroundColor: DS.surfaceSoft,
          minHeight: '100vh',
          overflowY: 'auto'
        }}
      >
        <Box sx={{
          px: { xs: 1.5, sm: 3 },
          pb: { xs: 1.5, sm: 3 },
          pt: isMobile || !desktopOpen ? 8 : 3,
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default InternalNavigation;
